import React, { useState } from "react";
import { generateCAD, getStlPreviewUrl, downloadCADFile } from "../api";
import { Canvas, useLoader } from "@react-three/fiber";
import { OrbitControls, PerspectiveCamera } from "@react-three/drei";
import { Suspense } from "react";
import { STLLoader } from "three-stdlib";

const STLModel: React.FC<{ url: string }> = ({ url }) => {
  const geometry = useLoader(STLLoader, url);
  return (
    <mesh geometry={geometry}>
      <meshStandardMaterial color="orange" metalness={0.3} roughness={0.4} />
    </mesh>
  );
};

const Home: React.FC = () => {
  const [prompt, setPrompt] = useState("");
  const [scadCode, setScadCode] = useState("");
  const [stlUrl, setStlUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [downloadingFormat, setDownloadingFormat] = useState<string | null>(null);

  const generateModel = async () => {
    if (!prompt.trim()) return;
    setLoading(true);
    setError("");
    setStlUrl("");
    setScadCode("");

    try {
      const data = await generateCAD(prompt);
      setScadCode(data.scad_code);

      const blobUrl = await getStlPreviewUrl(data.scad_code);
      setStlUrl(blobUrl);
    } catch (err: any) {
      console.error("Backend error:", err);
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (format: "stl" | "3mf" | "scad") => {
    if (!scadCode || downloadingFormat) return;
    setDownloadingFormat(format);
    try {
      await downloadCADFile(scadCode, format);
    } catch (err: any) {
      setError(err.message || `Failed to download .${format}`);
    } finally {
      setDownloadingFormat(null);
    }
  };

  return (
    <div className="flex h-screen bg-gradient-to-r from-zinc-950 via-zinc-900 to-black font-sans">
      <div className="w-1/2 flex flex-col justify-center items-center px-12">
        <div className="w-full max-w-xl bg-zinc-900 p-8 rounded-xl shadow-xl border border-zinc-800">
          <div className="flex justify-between items-start mb-6">
            <h1 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-fuchsia-500 to-pink-500 tracking-tight drop-shadow-md animate-fade-in">
              Text-to-CAD Generator
            </h1>
            <span className="text-xs font-medium px-2.5 py-1 rounded bg-fuchsia-500/10 text-fuchsia-400 border border-fuchsia-500/20 mt-2">
              Limit: 5/hr
            </span>
          </div>

          <textarea
            className="flex-1 w-full bg-[#1A1A1A] text-white placeholder-gray-400 border border-[#333] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-fuchsia-500 focus:border-transparent transition-all duration-300 shadow-inner resize-y min-h-[100px]"
            placeholder="Describe your 3D model in natural language..."
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            disabled={loading}
            rows={4}
          />

          {error && (
            <p className="text-red-400 text-sm mt-4 bg-red-900/20 p-3 rounded-lg border border-red-500/30">
              {error}
            </p>
          )}

          <button
            onClick={generateModel}
            disabled={loading || !prompt.trim()}
            className="mt-6 w-full py-3 px-6 text-white font-semibold rounded-full bg-gradient-to-r from-purple-600 to-fuchsia-600 hover:from-fuchsia-600 hover:to-purple-600 transition-all duration-300 shadow-md hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-fuchsia-400 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Generating..." : "Generate 3D Model"}
          </button>

          {scadCode && (
            <div className="mt-6 pt-6 border-t border-zinc-800 animate-fade-in">
              <p className="text-sm text-zinc-400 mb-3 font-medium">Export Model:</p>
              <div className="flex gap-3">
                <button onClick={() => handleDownload("stl")} disabled={!!downloadingFormat} className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 py-2 px-4 rounded-lg text-sm font-medium transition-colors border border-zinc-700 disabled:opacity-50">
                  {downloadingFormat === "stl" ? "Exporting..." : ".STL"}
                </button>
                <button onClick={() => handleDownload("3mf")} disabled={!!downloadingFormat} className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 py-2 px-4 rounded-lg text-sm font-medium transition-colors border border-zinc-700 disabled:opacity-50">
                  {downloadingFormat === "3mf" ? "Exporting..." : ".3MF (Color)"}
                </button>
                <button onClick={() => handleDownload("scad")} disabled={!!downloadingFormat} className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 py-2 px-4 rounded-lg text-sm font-medium transition-colors border border-zinc-700 disabled:opacity-50">
                  .SCAD Code
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="w-1/2 flex items-center justify-center">
        <div className="w-[85%] h-[85%] bg-zinc-900 rounded-2xl border border-zinc-800 shadow-inner flex items-center justify-center">
          {stlUrl ? (
            <div className="w-full h-full">
              <Canvas>
                <ambientLight intensity={0.5} />
                <pointLight position={[10, 10, 10]} />
                <PerspectiveCamera makeDefault position={[0, 0, 100]} />
                <OrbitControls />
                <Suspense fallback={null}>
                  <STLModel url={stlUrl} />
                </Suspense>
              </Canvas>
            </div>
          ) : (
            <div className="text-center text-zinc-400">
              <div className="text-6xl mb-4"></div>
              <p className="text-xl font-medium">3D Model Preview</p>
              <p className="text-sm mt-2">Generate a model to see it here</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Home;